import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { VitalityAssessment } from "@/types/vitality";
import { Save, FileDown, ChevronLeft, ChevronRight, Loader2, Baby, Activity } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { QUESTIONNAIRE_CONFIGS, QUESTIONNAIRE_TYPES, getResponseLabel } from "@/data/welcomeQuestionnaireData";
import { ChildVitalityForm, CHILD_VITALITY_TABS } from "@/components/vitality/ChildVitalityForm";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { useDraftPersistence } from "@/hooks/useDraftPersistence";
const VitalityAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdFromUrl = searchParams.get("patientId");
  const assessmentIdFromUrl = searchParams.get("assessmentId");

  const [assessment, setAssessment] = useState<VitalityAssessment>({
    patient_id: patientIdFromUrl || undefined,
  });
  const [patients, setPatients] = useState<any[]>([]);
  const [patientChildren, setPatientChildren] = useState<any[]>([]);
  const [selectedPatientGender, setSelectedPatientGender] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState("fiche-client");
  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false);
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false);

  // Local draft persistence — survives network errors / browser crashes
  const draftKey = assessment.id
    ? `vitality:${assessment.id}`
    : assessment.patient_id
    ? `vitality:new:${assessment.patient_id}${assessment.child_id ? `:${assessment.child_id}` : ""}`
    : null;
  const { storedDraft, clearDraft } = useDraftPersistence(draftKey, assessment, {
    delayMs: 2000,
  });
  const draftPromptedRef = useRef(false);

  useEffect(() => {
    if (!storedDraft || draftPromptedRef.current) return;
    if (isLoadingAssessment || isLoadingQuestionnaire) return;
    draftPromptedRef.current = true;
    const ageMs = Date.now() - new Date(storedDraft.savedAt).getTime();
    if (ageMs > 7 * 24 * 3600 * 1000) {
      clearDraft();
      return;
    }
    if (JSON.stringify(storedDraft.data) === JSON.stringify(assessment)) return;
    toast({
      title: "Brouillon non enregistré détecté",
      description: `Modifications du ${new Date(storedDraft.savedAt).toLocaleString("fr-FR")} restaurées.`,
    });
    setAssessment(storedDraft.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedDraft, isLoadingAssessment, isLoadingQuestionnaire]);

  // Guards against stale async updates when switching children
  const activeChildIdRef = useRef<string | null>(null);

  // Load existing assessment if assessmentId is provided
  const loadExistingAssessment = async (assessmentId: string) => {
    setIsLoadingAssessment(true);
    try {
      const { data, error } = await supabase
        .from("vitality_assessments")
        .select("*")
        .eq("id", assessmentId)
        .single();

      if (error) {
        console.error("Error loading assessment:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger le bilan",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        // Convert JSONB fields properly
        setAssessment(data as VitalityAssessment);
        
        // Load patient details if patient_id exists
        if (data.patient_id) {
          await loadPatientDetails(data.patient_id);
        }
        
        // Set the gender if available
        if (data.selected_gender) {
          setSelectedPatientGender(data.selected_gender);
        }
        
        // Set the correct tab based on assessment type
        if (data.child_id) {
          setCurrentTab("infos-generales");
        } else {
          setCurrentTab("fiche-client");
        }
        
        toast({
          title: "Bilan chargé",
          description: "Le bilan de vitalité a été chargé avec succès",
        });
      }
    } catch (error) {
      console.error("Error loading assessment:", error);
    } finally {
      setIsLoadingAssessment(false);
    }
  };

  // Function declarations need to be before useEffect calls that reference them
  const loadPatients = async () => {
    const { data, error } = await supabase.from("patients").select("id, name, gender, date_of_birth").order("name");

    if (error) {
      console.error("Error loading patients:", error);
      return;
    }

    setPatients(data || []);
  };

  // Load patient details and children when patient is selected
  const loadPatientDetails = async (patientId: string) => {
    try {
      // Load patient gender
      const patient = patients.find(p => p.id === patientId);
      if (patient?.gender) {
        setSelectedPatientGender(patient.gender);
        setAssessment(prev => ({ ...prev, selected_gender: patient.gender }));
      }

      // Load patient children
      const { data: childrenData, error: childrenError } = await supabase
        .from("patient_children")
        .select("*")
        .eq("parent_id", patientId)
        .order("created_at", { ascending: true });

      if (childrenError) {
        console.error("Error loading children:", childrenError);
        return;
      }

      setPatientChildren(childrenData || []);
    } catch (error) {
      console.error("Error loading patient details:", error);
    }
  };

  // Function to load and pre-fill data from child's welcome questionnaire
  const loadChildQuestionnaireData = async (patientId: string, childId: string) => {
    setIsLoadingQuestionnaire(true);
    try {
      // Find child info
      const child = patientChildren.find(c => c.id === childId);
      if (child) {
        // Set child gender
        if (child.gender) {
          setSelectedPatientGender(child.gender);
          setAssessment(prev => ({ ...prev, selected_gender: child.gender }));
        }
      }

      let questionnaire: any = null;

      // STRATEGY 1: Try to find questionnaire directly linked to patient with children type
      const { data: directQuestionnaires, error: directError } = await supabase
        .from("welcome_questionnaires")
        .select("*")
        .eq("patient_id", patientId)
        .eq("questionnaire_type", "naturopathy_children")
        .order("created_at", { ascending: false })
        .limit(1);

      if (!directError && directQuestionnaires && directQuestionnaires.length > 0) {
        questionnaire = directQuestionnaires[0];
        console.log("Found child questionnaire directly linked to patient:", questionnaire.id);
      }

      // STRATEGY 2: Try via appointments linked to patient
      if (!questionnaire) {
        const { data: appointments, error: appointmentsError } = await supabase
          .from("appointments")
          .select("id, service_type")
          .eq("patient_id", patientId)
          .order("appointment_date", { ascending: false });

        if (!appointmentsError && appointments && appointments.length > 0) {
          for (const appointment of appointments) {
            const serviceType = appointment.service_type?.toLowerCase() || "";
            if (!serviceType.includes("enfant") && !serviceType.includes("ado")) {
              continue;
            }

            const { data: aptQuestionnaire, error: questionnaireError } = await supabase
              .from("welcome_questionnaires")
              .select("*")
              .eq("appointment_id", appointment.id)
              .maybeSingle();

            if (!questionnaireError && aptQuestionnaire) {
              questionnaire = aptQuestionnaire;
              console.log("Found child questionnaire via appointment:", questionnaire.id);
              break;
            }
          }
        }
      }

      // STRATEGY 3: Try via patient email matching appointment email
      if (!questionnaire) {
        const { data: patientData } = await supabase
          .from("patients")
          .select("email")
          .eq("id", patientId)
          .maybeSingle();

        if (patientData?.email) {
          const { data: emailAppointments } = await supabase
            .from("appointments")
            .select("id, service_type")
            .ilike("email", patientData.email)
            .order("appointment_date", { ascending: false })
            .limit(10);

          if (emailAppointments && emailAppointments.length > 0) {
            for (const appointment of emailAppointments) {
              const serviceType = appointment.service_type?.toLowerCase() || "";
              if (!serviceType.includes("enfant") && !serviceType.includes("ado")) {
                continue;
              }

              const appointmentIds = [appointment.id];
              const { data: emailQuestionnaires } = await supabase
                .from("welcome_questionnaires")
                .select("*")
                .in("appointment_id", appointmentIds)
                .eq("questionnaire_type", "naturopathy_children")
                .order("created_at", { ascending: false })
                .limit(1);

              if (emailQuestionnaires && emailQuestionnaires.length > 0) {
                questionnaire = emailQuestionnaires[0];
                console.log("Found child questionnaire via email match:", questionnaire.id);
                break;
              }
            }
          }
        }
      }

      if (!questionnaire) {
        console.log("No child questionnaire found for patient");
        return;
      }

      // Ignore stale results if user switched child during the async flow
      if (activeChildIdRef.current !== childId) return;

      const responses = questionnaire.responses as Record<string, any>;
      if (!responses) return;

      // Safety: only apply questionnaire if it matches the selected child (name or DOB)
      // Note: 'child' is already declared at the top of this function
      const normalize = (s: string) => s.trim().toLowerCase();
      const qName = typeof responses.prenom_enfant === "string" ? normalize(responses.prenom_enfant) : null;
      const childName = child?.name ? normalize(child.name) : null;
      let qDobIso: string | null = null;
      if (responses.date_naissance_enfant?.day && responses.date_naissance_enfant?.month && responses.date_naissance_enfant?.year) {
        const dd = String(responses.date_naissance_enfant.day).padStart(2, "0");
        const mm = String(responses.date_naissance_enfant.month).padStart(2, "0");
        const yyyy = String(responses.date_naissance_enfant.year);
        qDobIso = `${yyyy}-${mm}-${dd}`;
      }
      const childDobIso = child?.date_of_birth ? String(child.date_of_birth).slice(0, 10) : null;

      const matchesChild =
        (qName && childName && qName === childName) ||
        (qDobIso && childDobIso && qDobIso === childDobIso);

      if (!matchesChild) {
        console.log("Child questionnaire mismatch: skip prefill", {
          requestedChildId: childId,
          qName,
          childName,
          qDobIso,
          childDobIso,
          questionnaireId: questionnaire.id,
        });
        return;
      }

      // Get the questionnaire config for naturopathy children
      const config = QUESTIONNAIRE_CONFIGS[QUESTIONNAIRE_TYPES.NATUROPATHY_CHILDREN];
      
      // Pre-fill the assessment fields from questionnaire responses
      const updates: Partial<VitalityAssessment> = {};

      // NOTE: we intentionally do NOT override child_name/child_age from questionnaire here,
      // to avoid showing another child's identity (the selector is the source of truth).

      // Question 3: Motif principal -> consultation_reason
      if (responses.motif_principal) {
        const motifQuestion = config.questions.find(q => q.id === "motif_principal");
        if (motifQuestion) {
          let motifText = getResponseLabel(motifQuestion, responses.motif_principal);
          if (responses.motif_autre) {
            motifText += ` - ${responses.motif_autre}`;
          }
          updates.consultation_reason = motifText;
        }
      }

      // Question 4: Attentes -> consultation_expectations
      if (responses.attentes) {
        updates.consultation_expectations = responses.attentes;
      }

      // Question 5: Traitement médical -> medical_treatments
      if (responses.traitement_medical) {
        const traitementQuestion = config.questions.find(q => q.id === "traitement_medical");
        if (traitementQuestion) {
          let traitementText = getResponseLabel(traitementQuestion, responses.traitement_medical);
          if (responses.traitement_medical_detail) {
            traitementText += ` : ${responses.traitement_medical_detail}`;
          }
          updates.medical_treatments = traitementText;
        }
      }

      // Question 6: Pathologie diagnostiquée -> pathologie_diagnostiquee
      if (responses.pathologie_diagnostiquee) {
        const pathoQuestion = config.questions.find(q => q.id === "pathologie_diagnostiquee");
        if (pathoQuestion) {
          let pathoText = getResponseLabel(pathoQuestion, responses.pathologie_diagnostiquee);
          if (responses.pathologie_diagnostiquee_detail) {
            pathoText += ` : ${responses.pathologie_diagnostiquee_detail}`;
          }
          updates.pathologie_diagnostiquee = pathoText;
        }
      }

      // Question 7: Niveau d'énergie -> energy_level
      if (responses.niveau_energie) {
        updates.energy_level = parseInt(responses.niveau_energie, 10);
      }

      // Apply updates to assessment
      if (Object.keys(updates).length > 0) {
        if (activeChildIdRef.current !== childId) return;
        setAssessment(prev => {
          if (activeChildIdRef.current !== childId) return prev;
          return {
            ...prev,
            ...updates,
          };
        });
        
        toast({
          title: "Données pré-remplies",
          description: "Les informations du questionnaire de bienvenue (enfant) ont été importées",
        });
      }
    } catch (error) {
      console.error("Error loading child questionnaire:", error);
    } finally {
      setIsLoadingQuestionnaire(false);
    }
  };

  useEffect(() => {
    loadPatients();
    // Load existing assessment if assessmentId is in URL
    if (assessmentIdFromUrl) {
      loadExistingAssessment(assessmentIdFromUrl);
    }
  }, []);

  // Load welcome questionnaire data when patient is selected (for adults)
  // or when child is selected (for children)
  useEffect(() => {
    if (assessment.patient_id && !assessment.id) {
      loadPatientDetails(assessment.patient_id);
      // Only load adult questionnaire if no child is selected
      if (!assessment.child_id) {
        loadWelcomeQuestionnaireData(assessment.patient_id);
      }
    }
  }, [assessment.patient_id]);

  // Load child questionnaire when child is selected
  useEffect(() => {
    if (assessment.child_id && assessment.patient_id && !assessment.id) {
      activeChildIdRef.current = assessment.child_id;
      loadChildQuestionnaireData(assessment.patient_id, assessment.child_id);
    }
  }, [assessment.child_id, assessment.patient_id, assessment.id]);



  // Function to load and pre-fill data from welcome questionnaire
  const loadWelcomeQuestionnaireData = async (patientId: string) => {
    setIsLoadingQuestionnaire(true);
    try {
      let questionnaire: any = null;

      // STRATEGY 1: Try to find questionnaire directly linked to patient
      const { data: directQuestionnaires, error: directError } = await supabase
        .from("welcome_questionnaires")
        .select("*")
        .eq("patient_id", patientId)
        .in("questionnaire_type", ["naturopathy_adults", "default"])
        .order("created_at", { ascending: false })
        .limit(1);

      if (!directError && directQuestionnaires && directQuestionnaires.length > 0) {
        questionnaire = directQuestionnaires[0];
        console.log("Found questionnaire directly linked to patient:", questionnaire.id);
      }

      // STRATEGY 2: If no direct link, try via appointments
      if (!questionnaire) {
        const { data: appointments, error: appointmentsError } = await supabase
          .from("appointments")
          .select("id, service_type")
          .eq("patient_id", patientId)
          .order("appointment_date", { ascending: false });

        if (!appointmentsError && appointments && appointments.length > 0) {
          for (const appointment of appointments) {
            const { data: aptQuestionnaire, error: questionnaireError } = await supabase
              .from("welcome_questionnaires")
              .select("*")
              .eq("appointment_id", appointment.id)
              .single();

            if (!questionnaireError && aptQuestionnaire) {
              questionnaire = aptQuestionnaire;
              console.log("Found questionnaire via appointment:", questionnaire.id);
              break;
            }
          }
        }
      }

      // STRATEGY 3: Try to find via patient email and appointment email match
      if (!questionnaire) {
        // Get patient email
        const { data: patientData } = await supabase
          .from("patients")
          .select("email")
          .eq("id", patientId)
          .single();

        if (patientData?.email) {
          // Find appointments by email
          const { data: emailAppointments } = await supabase
            .from("appointments")
            .select("id")
            .ilike("email", patientData.email)
            .order("appointment_date", { ascending: false })
            .limit(5);

          if (emailAppointments && emailAppointments.length > 0) {
            const appointmentIds = emailAppointments.map(a => a.id);
            const { data: emailQuestionnaires } = await supabase
              .from("welcome_questionnaires")
              .select("*")
              .in("appointment_id", appointmentIds)
              .in("questionnaire_type", ["naturopathy_adults", "default"])
              .order("created_at", { ascending: false })
              .limit(1);

            if (emailQuestionnaires && emailQuestionnaires.length > 0) {
              questionnaire = emailQuestionnaires[0];
              console.log("Found questionnaire via email match:", questionnaire.id);
            }
          }
        }
      }

      if (!questionnaire) {
        console.log("No questionnaire found for patient");
        return;
      }

      const responses = questionnaire.responses as Record<string, any>;
      if (!responses) return;

      // Get the questionnaire config for naturopathy adults
      const config = QUESTIONNAIRE_CONFIGS[QUESTIONNAIRE_TYPES.NATUROPATHY_ADULTS];
      
      // Pre-fill the assessment fields from questionnaire responses
      const updates: Partial<VitalityAssessment> = {};

      // Question 1: Motif principal -> consultation_reason
      if (responses.motif_principal) {
        const motifQuestion = config.questions.find(q => q.id === "motif_principal");
        if (motifQuestion) {
          let motifText = getResponseLabel(motifQuestion, responses.motif_principal);
          if (responses.motif_autre) {
            motifText += ` - ${responses.motif_autre}`;
          }
          updates.consultation_reason = motifText;
        }
      }

      // Question 2: Attentes -> consultation_expectations
      if (responses.attentes) {
        updates.consultation_expectations = responses.attentes;
      }

      // Question 3: Traitement médical -> medical_treatments
      if (responses.traitement_medical) {
        const traitementQuestion = config.questions.find(q => q.id === "traitement_medical");
        if (traitementQuestion) {
          let traitementText = getResponseLabel(traitementQuestion, responses.traitement_medical);
          if (responses.traitement_medical_text) {
            traitementText += ` : ${responses.traitement_medical_text}`;
          }
          updates.medical_treatments = traitementText;
        }
      }

      // Question 4: Pathologie diagnostiquée -> pathologie_diagnostiquee
      if (responses.pathologie_diagnostiquee) {
        const pathoQuestion = config.questions.find(q => q.id === "pathologie_diagnostiquee");
        if (pathoQuestion) {
          let pathoText = getResponseLabel(pathoQuestion, responses.pathologie_diagnostiquee);
          if (responses.pathologie_diagnostiquee_text) {
            pathoText += ` : ${responses.pathologie_diagnostiquee_text}`;
          }
          updates.pathologie_diagnostiquee = pathoText;
        }
      }

      // Question 5: Niveau d'énergie -> energy_level
      if (responses.niveau_energie) {
        updates.energy_level = parseInt(responses.niveau_energie, 10);
      }

      // Apply updates to assessment
      if (Object.keys(updates).length > 0) {
        setAssessment(prev => ({
          ...prev,
          ...updates,
        }));
        
        toast({
          title: "Données pré-remplies",
          description: "Les informations du questionnaire de bienvenue ont été importées",
        });
      }
    } catch (error) {
      console.error("Error loading welcome questionnaire:", error);
    } finally {
      setIsLoadingQuestionnaire(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Erreur",
          description: "Vous devez être connecté pour sauvegarder un bilan",
          variant: "destructive",
        });
        return;
      }

      if (!assessment.patient_id) {
        toast({
          title: "Patient requis",
          description: "Veuillez sélectionner un patient",
          variant: "destructive",
        });
        return;
      }

      const dataToSave = {
        ...assessment,
        user_id: user.id,
      };

      if (assessment.id) {
        const { error } = await supabase.from("vitality_assessments").update(dataToSave).eq("id", assessment.id);

        if (error) throw error;

        clearDraft();
        toast({
          title: "Bilan mis à jour",
          description: "Le bilan de vitalité a été mis à jour avec succès",
        });
      } else {
        const { data, error } = await supabase.from("vitality_assessments").insert([dataToSave]).select().single();

        if (error) throw error;

        clearDraft();
        setAssessment({ ...assessment, id: data.id });

        toast({
          title: "Bilan créé",
          description: "Le bilan de vitalité a été créé avec succès",
        });
      }
    } catch (error: any) {
      console.error("Error saving assessment:", error);
      toast({
        title: "Erreur",
        description: (error?.message || "Impossible de sauvegarder le bilan") + " — Brouillon conservé localement.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!assessment.id) {
      toast({
        title: "Erreur",
        description: "Veuillez d'abord sauvegarder le bilan avant de l'exporter",
        variant: "destructive",
      });
      return;
    }

    if (!assessment.patient_id) {
      toast({
        title: "Erreur",
        description: "Le bilan doit être lié à un patient",
        variant: "destructive",
      });
      return;
    }

    try {
      const patient = patients.find((p) => p.id === assessment.patient_id);
      const patientName = patient?.name || "Patient inconnu";

      // Use child-specific PDF for child assessments
      if (isChildAssessment) {
        const { generateChildVitalityAssessmentPDF } = await import("@/utils/childVitalityPdfExport");
        generateChildVitalityAssessmentPDF(assessment, patientName);
      } else {
        const { generateVitalityAssessmentPDF } = await import("@/utils/pdfExport");
        generateVitalityAssessmentPDF(assessment, patientName);
      }

      toast({
        title: "PDF généré",
        description: "Le bilan de vitalité a été exporté avec succès",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le PDF",
        variant: "destructive",
      });
    }
  };

  // Determine if this is a child assessment
  const isChildAssessment = !!assessment.child_id;

  const updateField = (field: keyof VitalityAssessment, value: any) => {
    setAssessment((prev) => ({ ...prev, [field]: value }));
  };

  const tabs = [
    { id: "fiche-client", label: "Fiche Client" },
    { id: "sphere-pro", label: "Sphère Pro/Perso" },
    { id: "medical", label: "Historique Médical" },
    { id: "life-history", label: "Historique de Vie" },
    { id: "hygiene", label: "Hygiène de Vie" },
    { id: "vitalite", label: "Vitalité" },
    { id: "systemes", label: "Systèmes" },
    { id: "alimentation", label: "Alimentation" },
    { id: "recap", label: "Récapitulatif" },
  ];

  const currentTabIndex = tabs.findIndex((tab) => tab.id === currentTab);
  const progressPercentage = ((currentTabIndex + 1) / tabs.length) * 100;

  const weekDays = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
  const mealTimes = ["matin", "midi", "collation", "soir"];

  const updateWeeklyMenu = (day: string, meal: string, value: string) => {
    setAssessment((prev) => ({
      ...prev,
      weekly_menu: {
        ...prev.weekly_menu,
        [day]: {
          ...(prev.weekly_menu?.[day as keyof typeof prev.weekly_menu] || {}),
          [meal]: value,
        },
      },
    }));
  };

  // Get parent name for child form
  const getParentName = () => {
    const patient = patients.find((p) => p.id === assessment.patient_id);
    return patient?.name || "";
  };

  // Get current tabs based on assessment type
  const getCurrentTabs = () => {
    if (isChildAssessment) {
      return CHILD_VITALITY_TABS;
    }
    return tabs;
  };

  const currentTabs = getCurrentTabs();
  const currentTabIndexDynamic = currentTabs.findIndex((tab) => tab.id === currentTab);
  const progressPercentageDynamic = ((currentTabIndexDynamic + 1) / currentTabs.length) * 100;

  return (
    <PageContainer>
      <PageHeader
        title={isChildAssessment ? "Bilan de Vitalité - Enfant" : "Bilan de Vitalité"}
        subtitle={isChildAssessment ? "Évaluation naturopathique adaptée à l'enfant" : "Évaluation complète naturopathique"}
        icon={isChildAssessment ? <Baby className="w-6 h-6 sm:w-7 sm:h-7" /> : <Activity className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
      />
      <div className="space-y-4 sm:space-y-6">

        {/* Patient/Child Selection Card - Always visible */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Sélection du consultant</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="patient">Consultant (parent) *</Label>
                <div className="flex items-center gap-2">
                  <Select 
                    value={assessment.patient_id || "none"} 
                    onValueChange={(value) => {
                      if (value === "none") {
                        updateField("patient_id", undefined);
                      } else {
                        updateField("patient_id", value);
                      }
                      // Reset child selection and switch to adult form
                      updateField("child_id", undefined);
                      updateField("is_child_assessment", false);
                      setPatientChildren([]);
                      setCurrentTab("fiche-client");
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Sélectionner un consultant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sélectionner un consultant</SelectItem>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id}>
                          {patient.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLoadingQuestionnaire && (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  )}
                </div>
              </div>

              {/* Child selector - only show if patient has children */}
              {assessment.patient_id && patientChildren.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="child" className="flex items-center gap-2">
                    <Baby className="h-4 w-4 text-primary" />
                    Bilan pour un enfant ?
                  </Label>
                  <Select 
                    value={assessment.child_id || "none"} 
                    onValueChange={(value) => {
                      if (value === "none") {
                        // Reset all child-related fields when switching to adult
                        setAssessment(prev => ({
                          ...prev,
                          child_id: undefined,
                          is_child_assessment: false,
                          child_name: undefined,
                          child_age: undefined,
                          child_date_of_birth: undefined,
                          child_last_name: undefined,
                          child_family_situation: undefined,
                          child_recent_changes: undefined,
                          child_school_info: undefined,
                          child_birth_type: undefined,
                          child_feeding_type: undefined,
                          child_pregnancy_notes: undefined,
                          child_medical_history: undefined,
                          child_current_treatments: undefined,
                          child_antibiotics_history: undefined,
                          child_medical_notes: undefined,
                          child_typical_day: undefined,
                          child_hobbies: undefined,
                          child_physical_activity: undefined,
                          child_screen_time: undefined,
                          child_hygiene_notes: undefined,
                          child_sleep_schedule: undefined,
                          child_falling_asleep: undefined,
                          child_night_wakings: undefined,
                          child_restful_sleep: undefined,
                          child_breakfast: undefined,
                          child_meals_type: undefined,
                          child_snacks: undefined,
                          child_preferred_foods: undefined,
                          child_refused_foods: undefined,
                          child_drinks: undefined,
                          child_weekly_menu: undefined,
                          child_transit: undefined,
                          child_digestive_issues: undefined,
                          child_digestion_notes: undefined,
                          child_emotions: undefined,
                          child_observed_behaviors: undefined,
                          child_expressed_emotions: undefined,
                          child_energy_level: undefined,
                          child_fatigue: undefined,
                          child_immunity: undefined,
                          child_priorities: undefined,
                          child_support_axes: undefined,
                          child_recommendations: undefined,
                        }));
                        // Reset to parent gender
                        const patient = patients.find(p => p.id === assessment.patient_id);
                        if (patient?.gender) {
                          setSelectedPatientGender(patient.gender);
                        }
                        // Switch to adult form tabs
                        setCurrentTab("fiche-client");
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } else {
                        // Find the selected child
                        const child = patientChildren.find(c => c.id === value);
                        
                        if (!child) {
                          console.error("Child not found:", value);
                          return;
                        }
                        
                        // Calculate age from date of birth
                        let calculatedAge = "";
                        if (child.date_of_birth) {
                          const birthDate = new Date(child.date_of_birth);
                          const today = new Date();
                          let age = today.getFullYear() - birthDate.getFullYear();
                          const monthDiff = today.getMonth() - birthDate.getMonth();
                          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                          }
                          calculatedAge = `${age}`;
                        }
                        
                        console.log("Selecting child:", child.name, "DOB:", child.date_of_birth, "Age:", calculatedAge);
                        
                        // Check if there's an existing assessment for this child
                        const loadExistingChildAssessment = async () => {
                          try {
                            const { data: existingAssessment, error } = await supabase
                              .from("vitality_assessments")
                              .select("*")
                              .eq("patient_id", assessment.patient_id)
                              .eq("child_id", value)
                              .order("created_at", { ascending: false })
                              .limit(1)
                              .maybeSingle();
                            
                            if (error) {
                              console.error("Error loading existing child assessment:", error);
                            }
                            
                            if (existingAssessment) {
                              console.log("Found existing assessment for child:", child.name, existingAssessment);
                              // Load existing assessment data
                              setAssessment({
                                ...existingAssessment as VitalityAssessment,
                                child_name: child.name, // Ensure correct child name
                                child_age: calculatedAge, // Recalculate age
                                child_date_of_birth: child.date_of_birth || "",
                                selected_gender: child.gender || existingAssessment.selected_gender || "",
                              });
                              toast({
                                title: "Bilan existant chargé",
                                description: `Le bilan de ${child.name} a été chargé`,
                              });
                            } else {
                              console.log("No existing assessment for child, creating new:", child.name);
                              // Reset and set new child data
                              setAssessment(prev => ({
                                patient_id: prev.patient_id,
                                user_id: prev.user_id,
                                id: undefined,
                                child_id: value,
                                is_child_assessment: true,
                                child_name: child.name,
                                child_age: calculatedAge,
                                child_date_of_birth: child.date_of_birth || "",
                                selected_gender: child.gender || "",
                              }));
                            }
                          } catch (err) {
                            console.error("Error in loadExistingChildAssessment:", err);
                            // Fallback: create new assessment
                            setAssessment(prev => ({
                              patient_id: prev.patient_id,
                              user_id: prev.user_id,
                              id: undefined,
                              child_id: value,
                              is_child_assessment: true,
                              child_name: child.name,
                              child_age: calculatedAge,
                              child_date_of_birth: child.date_of_birth || "",
                              selected_gender: child.gender || "",
                            }));
                          }
                        };
                        
                        loadExistingChildAssessment();
                        
                        if (child.gender) {
                          setSelectedPatientGender(child.gender);
                        }
                        // Switch to child form tabs
                        setCurrentTab("infos-generales");
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un enfant (optionnel)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun - Bilan pour l'adulte</SelectItem>
                      {patientChildren.map((child) => {
                        let childAge = "";
                        if (child.date_of_birth) {
                          const birthDate = new Date(child.date_of_birth);
                          const today = new Date();
                          let age = today.getFullYear() - birthDate.getFullYear();
                          const monthDiff = today.getMonth() - birthDate.getMonth();
                          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                          }
                          childAge = `(${age} ans)`;
                        }
                        return (
                          <SelectItem key={child.id} value={child.id}>
                            {child.name} {childAge}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Show child indicator */}
            {isChildAssessment && assessment.child_name && (
              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20 flex items-center gap-3">
                <Baby className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium text-primary">
                    Bilan pour {assessment.child_name} {assessment.child_age && `(${assessment.child_age}${!assessment.child_age.includes('ans') ? ' ans' : ''})`}
                  </p>
                  <p className="text-sm text-muted-foreground">Parent: {getParentName()}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conditional Form Rendering */}
        {isChildAssessment ? (
          <>
            {/* Child Form */}
            <ChildVitalityForm
              assessment={assessment}
              updateField={updateField}
              parentName={getParentName()}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
            />

            {/* Save/Export buttons for child form */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="gap-2"
                    size="lg"
                  >
                    <Save className="w-5 h-5" />
                    {isSaving ? "Enregistrement..." : "Enregistrer le bilan enfant"}
                  </Button>
                  <Button 
                    onClick={handleExportPDF} 
                    variant="outline"
                    className="gap-2"
                    size="lg"
                  >
                    <FileDown className="w-5 h-5" />
                    Exporter en PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Navigation for child form */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const idx = CHILD_VITALITY_TABS.findIndex((tab) => tab.id === currentTab);
                      if (idx > 0) {
                        setCurrentTab(CHILD_VITALITY_TABS[idx - 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={currentTabIndexDynamic === 0}
                    className="gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </Button>

                  <Button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    variant="secondary"
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? "..." : "Enregistrer"}
                  </Button>

                  <Button
                    onClick={() => {
                      const idx = CHILD_VITALITY_TABS.findIndex((tab) => tab.id === currentTab);
                      if (idx < CHILD_VITALITY_TABS.length - 1) {
                        setCurrentTab(CHILD_VITALITY_TABS[idx + 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={currentTabIndexDynamic === CHILD_VITALITY_TABS.length - 1}
                    className="gap-2"
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Adult Form - Original tabs structure */}
            {/* Progress for adult form */}
            {assessment.patient_id && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{tabs[currentTabIndex]?.label || ""}</span>
                      <span>{Math.round(progressPercentage)}%</span>
                    </div>
                    <Progress value={progressPercentage} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main Adult Form */}
            <Tabs value={currentTab} onValueChange={setCurrentTab}>
              {/* Mobile: horizontal scroll tabs */}
              <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
                <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-5 lg:grid-cols-9 gap-1 min-w-full sm:min-w-0">
                  {tabs.map((tab) => (
                    <TabsTrigger 
                      key={tab.id} 
                      value={tab.id} 
                      className="text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap flex-shrink-0"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

          {/* Fiche Client */}
          <TabsContent value="fiche-client">
            <Card>
              <CardHeader>
                <CardTitle>Fiche Client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="patient">Consultant *</Label>
                  <div className="flex items-center gap-2">
                    <Select 
                      value={assessment.patient_id} 
                      onValueChange={(value) => {
                        updateField("patient_id", value);
                        // Reset child selection when patient changes
                        updateField("child_id", undefined);
                        setPatientChildren([]);
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Sélectionner un consultant" />
                      </SelectTrigger>
                      <SelectContent>
                        {patients.map((patient) => (
                          <SelectItem key={patient.id} value={patient.id}>
                            {patient.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isLoadingQuestionnaire && (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )}
                  </div>
                  {isLoadingQuestionnaire && (
                    <p className="text-sm text-muted-foreground">Chargement des données du questionnaire de bienvenue...</p>
                  )}
                </div>

                {/* Genre biologique display */}
                {assessment.patient_id && selectedPatientGender && (
                  <div className="space-y-2">
                    <Label>Genre biologique</Label>
                    <div className="px-3 py-2 bg-muted/50 border rounded-md text-sm">
                      {selectedPatientGender === "femme" && "Femme"}
                      {selectedPatientGender === "homme" && "Homme"}
                      {selectedPatientGender === "intersexe" && "Intersexe"}
                      {selectedPatientGender === "prefere_ne_pas_repondre" && "Préfère ne pas répondre"}
                      {!["femme", "homme", "intersexe", "prefere_ne_pas_repondre"].includes(selectedPatientGender) && selectedPatientGender}
                    </div>
                  </div>
                )}

                {/* Child selector - only show if patient has children */}
                {assessment.patient_id && patientChildren.length > 0 && (
                  <div className="space-y-2 p-4 bg-secondary/20 rounded-lg border border-secondary/30">
                    <Label htmlFor="child" className="flex items-center gap-2">
                      <span className="text-primary font-medium">Bilan pour un enfant ?</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Ce consultant a {patientChildren.length} enfant(s) enregistré(s). Sélectionnez un enfant pour faire un bilan le concernant.
                    </p>
                    <Select 
                      value={assessment.child_id || "none"} 
                      onValueChange={(value) => {
                        if (value === "none") {
                          updateField("child_id", undefined);
                          // Reset to parent gender
                          const patient = patients.find(p => p.id === assessment.patient_id);
                          if (patient?.gender) {
                            setSelectedPatientGender(patient.gender);
                            updateField("selected_gender", patient.gender);
                          }
                        } else {
                          updateField("child_id", value);
                          // Load child gender
                          const child = patientChildren.find(c => c.id === value);
                          if (child?.gender) {
                            setSelectedPatientGender(child.gender);
                            updateField("selected_gender", child.gender);
                          }
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un enfant (optionnel)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun - Bilan pour le consultant adulte</SelectItem>
                        {patientChildren.map((child) => {
                          let childAge = "";
                          if (child.date_of_birth) {
                            const birthDate = new Date(child.date_of_birth);
                            const today = new Date();
                            let age = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                              age--;
                            }
                            childAge = `(${age} ans)`;
                          }
                          return (
                            <SelectItem key={child.id} value={child.id}>
                              {child.name} {childAge}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Show child info if selected */}
                {assessment.child_id && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="space-y-2">
                      <Label htmlFor="child_name">Prénom de l'enfant</Label>
                      <Input
                        id="child_name"
                        value={assessment.child_name || ""}
                        onChange={(e) => updateField("child_name", e.target.value)}
                        placeholder="Prénom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="child_age">Âge de l'enfant</Label>
                      <Input
                        id="child_age"
                        value={assessment.child_age || ""}
                        onChange={(e) => updateField("child_age", e.target.value)}
                        placeholder="Ex: 8 ans"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Poids (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      value={assessment.weight || ""}
                      onChange={(e) => updateField("weight", parseFloat(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height">Taille (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={assessment.height || ""}
                      onChange={(e) => updateField("height", parseFloat(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="blood_group">Groupe sanguin</Label>
                    <Select value={assessment.blood_group} onValueChange={(value) => updateField("blood_group", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultation_reason">Motif de la consultation</Label>
                  <Textarea
                    id="consultation_reason"
                    rows={4}
                    value={assessment.consultation_reason || ""}
                    onChange={(e) => updateField("consultation_reason", e.target.value)}
                    placeholder="Décrivez le motif principal de cette consultation..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultation_expectations">Qu'attendez-vous principalement de cette consultation ?</Label>
                  <Textarea
                    id="consultation_expectations"
                    rows={4}
                    value={assessment.consultation_expectations || ""}
                    onChange={(e) => updateField("consultation_expectations", e.target.value)}
                    placeholder="Décrivez vos attentes..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sphère Pro/Perso */}
          <TabsContent value="sphere-pro">
            <Card>
              <CardHeader>
                <CardTitle>Sphère Professionnelle et Personnelle</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="marital_status">Situation familiale</Label>
                  <Select
                    value={assessment.marital_status}
                    onValueChange={(value) => updateField("marital_status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celibataire">Célibataire</SelectItem>
                      <SelectItem value="couple">En couple</SelectItem>
                      <SelectItem value="marie">Marié(e)</SelectItem>
                      <SelectItem value="divorce">Divorcé(e)</SelectItem>
                      <SelectItem value="veuf">Veuf/Veuve</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="children">Nombre d'enfants</Label>
                    <Input
                      id="children"
                      type="number"
                      value={assessment.children || ""}
                      onChange={(e) => updateField("children", parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pregnancies">Nombre de grossesses</Label>
                    <Input
                      id="pregnancies"
                      type="number"
                      value={assessment.pregnancies || ""}
                      onChange={(e) => updateField("pregnancies", parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="family_situation_notes">Ressenti de la situation</Label>
                  <Textarea
                    id="family_situation_notes"
                    rows={3}
                    value={assessment.family_situation_notes || ""}
                    onChange={(e) => updateField("family_situation_notes", e.target.value)}
                    placeholder="Solitude, qualité des relations..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession_status">Situation professionnelle</Label>
                  <Select
                    value={assessment.profession_status}
                    onValueChange={(value) => updateField("profession_status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chomage">Au chômage</SelectItem>
                      <SelectItem value="etudiant">Étudiant(e)</SelectItem>
                      <SelectItem value="parent_foyer">Parent au foyer</SelectItem>
                      <SelectItem value="temps_plein">Temps plein</SelectItem>
                      <SelectItem value="temps_partiel">Temps partiel</SelectItem>
                      <SelectItem value="arret_maladie">Arrêt maladie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Conditions de travail</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {["Assis", "Debout", "Gestes répétitifs", "Polluants", "Travail de nuit", "Week-end"].map(
                      (condition) => (
                        <div key={condition} className="flex items-center space-x-2">
                          <Checkbox
                            id={condition}
                            checked={assessment.work_conditions?.[condition] || false}
                            onCheckedChange={(checked) => {
                              updateField("work_conditions", {
                                ...assessment.work_conditions,
                                [condition]: checked,
                              });
                            }}
                          />
                          <Label htmlFor={condition} className="text-sm font-normal">
                            {condition}
                          </Label>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="work_ambiance">Ambiance au travail</Label>
                    <Textarea
                      id="work_ambiance"
                      rows={2}
                      value={assessment.work_ambiance || ""}
                      onChange={(e) => updateField("work_ambiance", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="work_fulfilling">Travail épanouissant ?</Label>
                    <Textarea
                      id="work_fulfilling"
                      rows={2}
                      value={assessment.work_fulfilling || ""}
                      onChange={(e) => updateField("work_fulfilling", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="work_transport">Transport</Label>
                    <Input
                      id="work_transport"
                      value={assessment.work_transport || ""}
                      onChange={(e) => updateField("work_transport", e.target.value)}
                      placeholder="Voiture, transport en commun..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="work_stress">Niveau de stress</Label>
                    <Select value={assessment.work_stress} onValueChange={(value) => updateField("work_stress", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="faible">Faible</SelectItem>
                        <SelectItem value="moyen">Moyen</SelectItem>
                        <SelectItem value="eleve">Élevé</SelectItem>
                        <SelectItem value="tres_eleve">Très élevé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* NEW: Notes field */}
                <div className="border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="sphere_pro_notes">Notes</Label>
                    <Textarea
                      id="sphere_pro_notes"
                      rows={3}
                      value={assessment.sphere_pro_notes || ""}
                      onChange={(e) => updateField("sphere_pro_notes", e.target.value)}
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Historique Médical */}
          <TabsContent value="medical">
            <Card>
              <CardHeader>
                <CardTitle>Historique Médical</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_symptoms">Symptômes actuels</Label>
                  <Textarea
                    id="current_symptoms"
                    rows={3}
                    value={assessment.current_symptoms || ""}
                    onChange={(e) => updateField("current_symptoms", e.target.value)}
                    placeholder="Troubles, maladie chronique..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="chronic_diseases">Maladies chroniques</Label>
                  <Textarea
                    id="chronic_diseases"
                    rows={2}
                    value={assessment.chronic_diseases || ""}
                    onChange={(e) => updateField("chronic_diseases", e.target.value)}
                  />
                </div>

                {/* NEW: Analyses biologiques récentes */}
                <div className="space-y-2">
                  <Label htmlFor="biological_analyses">Analyses biologiques récentes</Label>
                  <Textarea
                    id="biological_analyses"
                    rows={3}
                    value={assessment.biological_analyses || ""}
                    onChange={(e) => updateField("biological_analyses", e.target.value)}
                    placeholder="Résultats de bilans sanguins, urinaires..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hospitalizations">Hospitalisations</Label>
                    <Textarea
                      id="hospitalizations"
                      rows={2}
                      value={assessment.hospitalizations || ""}
                      onChange={(e) => updateField("hospitalizations", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="operations">Opérations</Label>
                    <Textarea
                      id="operations"
                      rows={2}
                      value={assessment.operations || ""}
                      onChange={(e) => updateField("operations", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medical_treatments">Traitements médicaux en cours</Label>
                  <Textarea
                    id="medical_treatments"
                    rows={2}
                    value={assessment.medical_treatments || ""}
                    onChange={(e) => updateField("medical_treatments", e.target.value)}
                    placeholder="Médicaments, pilule..."
                  />
                </div>

                {/* NEW: Pathologie diagnostiquée */}
                <div className="space-y-2">
                  <Label htmlFor="pathologie_diagnostiquee">Pathologie diagnostiquée par un professionnel de santé</Label>
                  <Textarea
                    id="pathologie_diagnostiquee"
                    rows={2}
                    value={assessment.pathologie_diagnostiquee || ""}
                    onChange={(e) => updateField("pathologie_diagnostiquee", e.target.value)}
                    placeholder="Pathologies diagnostiquées..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supplements">Compléments alimentaires</Label>
                  <Textarea
                    id="supplements"
                    rows={2}
                    value={assessment.supplements || ""}
                    onChange={(e) => updateField("supplements", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="essential_oils">Huiles essentielles</Label>
                  <Textarea
                    id="essential_oils"
                    rows={2}
                    value={assessment.essential_oils || ""}
                    onChange={(e) => updateField("essential_oils", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="birth_type">Type de naissance</Label>
                    <Select value={assessment.birth_type} onValueChange={(value) => updateField("birth_type", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cesarienne">Césarienne</SelectItem>
                        <SelectItem value="voie_basse">Voie basse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="breastfeeding">Allaitement</Label>
                    <Select
                      value={assessment.breastfeeding === true ? "oui" : assessment.breastfeeding === false ? "non" : ""}
                      onValueChange={(value) => updateField("breastfeeding", value === "oui" ? true : value === "non" ? false : undefined)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oui">Oui</SelectItem>
                        <SelectItem value="non">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Hérédité */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Hérédité</h3>
                  <Textarea
                    rows={3}
                    value={assessment.heredity?.notes || ""}
                    onChange={(e) =>
                      updateField("heredity", {
                        ...assessment.heredity,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Antécédents familiaux..."
                  />
                </div>

                {/* Suivi Médical / Paramédical */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Suivi Médical / Paramédical</h3>
                  <Textarea
                    rows={3}
                    value={assessment.medical_followup?.notes || ""}
                    onChange={(e) =>
                      updateField("medical_followup", {
                        ...assessment.medical_followup,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Médecin traitant, spécialistes, kinésithérapeute, ostéopathe..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Historique de Vie */}
          <TabsContent value="life-history">
            <Card>
              <CardHeader>
                <CardTitle>Historique de Vie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="life_childhood">Enfance</Label>
                  <Textarea
                    id="life_childhood"
                    rows={4}
                    value={assessment.life_history?.childhood || ""}
                    onChange={(e) =>
                      updateField("life_history", {
                        ...assessment.life_history,
                        childhood: e.target.value,
                      })
                    }
                    placeholder="Événements marquants de l'enfance..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="life_adolescence">Adolescence</Label>
                  <Textarea
                    id="life_adolescence"
                    rows={4}
                    value={assessment.life_history?.adolescence || ""}
                    onChange={(e) =>
                      updateField("life_history", {
                        ...assessment.life_history,
                        adolescence: e.target.value,
                      })
                    }
                    placeholder="Événements marquants de l'adolescence..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="life_adulthood">Adulte</Label>
                  <Textarea
                    id="life_adulthood"
                    rows={4}
                    value={assessment.life_history?.adulthood || ""}
                    onChange={(e) =>
                      updateField("life_history", {
                        ...assessment.life_history,
                        adulthood: e.target.value,
                      })
                    }
                    placeholder="Événements marquants de la vie adulte..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="life_today">Aujourd'hui</Label>
                  <Textarea
                    id="life_today"
                    rows={4}
                    value={assessment.life_history?.today || ""}
                    onChange={(e) =>
                      updateField("life_history", {
                        ...assessment.life_history,
                        today: e.target.value,
                      })
                    }
                    placeholder="Situation actuelle, ressenti..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hygiène de Vie */}
          <TabsContent value="hygiene">
            <Card>
              <CardHeader>
                <CardTitle>Hygiène de Vie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="typical_day">Journée type</Label>
                  <Textarea
                    id="typical_day"
                    rows={4}
                    value={assessment.typical_day || ""}
                    onChange={(e) => updateField("typical_day", e.target.value)}
                    placeholder="Décrivez une journée typique..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hobbies">Loisirs</Label>
                  <Textarea
                    id="hobbies"
                    rows={2}
                    value={assessment.hobbies || ""}
                    onChange={(e) => updateField("hobbies", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="physical_activity">Activité physique</Label>
                  <Textarea
                    id="physical_activity"
                    rows={2}
                    value={assessment.physical_activity || ""}
                    onChange={(e) => updateField("physical_activity", e.target.value)}
                    placeholder="Type, fréquence, durée..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="living_place">Lieu de vie</Label>
                  <Select value={assessment.living_place} onValueChange={(value) => updateField("living_place", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maison_campagne">Maison - Campagne</SelectItem>
                      <SelectItem value="maison_ville">Maison - Ville</SelectItem>
                      <SelectItem value="appartement_ville">Appartement - Ville</SelectItem>
                      <SelectItem value="appartement_campagne">Appartement - Campagne</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* NEW: Notes under Lieu de vie */}
                <div className="space-y-2">
                  <Label htmlFor="hygiene_notes">Notes</Label>
                  <Textarea
                    id="hygiene_notes"
                    rows={3}
                    value={assessment.hygiene_notes || ""}
                    onChange={(e) => updateField("hygiene_notes", e.target.value)}
                    placeholder="Notes complémentaires sur l'hygiène de vie..."
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Sommeil</h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="sleep_falling_asleep">Endormissement</Label>
                      <Input
                        id="sleep_falling_asleep"
                        value={assessment.sleep_quality?.falling_asleep || ""}
                        onChange={(e) =>
                          updateField("sleep_quality", {
                            ...assessment.sleep_quality,
                            falling_asleep: e.target.value,
                          })
                        }
                        placeholder="Facile, difficile, temps nécessaire..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sleep_night_wakings">Réveils nocturnes</Label>
                      <Input
                        id="sleep_night_wakings"
                        value={assessment.sleep_quality?.night_wakings || ""}
                        onChange={(e) =>
                          updateField("sleep_quality", {
                            ...assessment.sleep_quality,
                            night_wakings: e.target.value,
                          })
                        }
                        placeholder="Fréquence, raisons..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sleep_waking">Réveil</Label>
                      <Input
                        id="sleep_waking"
                        value={assessment.sleep_quality?.waking || ""}
                        onChange={(e) =>
                          updateField("sleep_quality", {
                            ...assessment.sleep_quality,
                            waking: e.target.value,
                          })
                        }
                        placeholder="Forme au réveil..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sleep_needs">Besoins de sommeil (heures)</Label>
                      <Input
                        id="sleep_needs"
                        value={assessment.sleep_quality?.needs || ""}
                        onChange={(e) =>
                          updateField("sleep_quality", {
                            ...assessment.sleep_quality,
                            needs: e.target.value,
                          })
                        }
                        placeholder="7-8h..."
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vitalité */}
          <TabsContent value="vitalite">
            <Card>
              <CardHeader>
                <CardTitle>Vitalité et Énergie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="temperament">Tempérament</Label>
                  <Select value={assessment.temperament} onValueChange={(value) => updateField("temperament", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nerveux">Nerveux</SelectItem>
                      <SelectItem value="bilieux">Bilieux</SelectItem>
                      <SelectItem value="sanguin">Sanguin</SelectItem>
                      <SelectItem value="lymphatique">Lymphatique</SelectItem>
                      <SelectItem value="mixte">Mixte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* NEW: Notes under Tempérament */}
                <div className="space-y-2">
                  <Label htmlFor="temperament_notes">Notes</Label>
                  <Textarea
                    id="temperament_notes"
                    rows={2}
                    value={assessment.temperament_notes || ""}
                    onChange={(e) => updateField("temperament_notes", e.target.value)}
                    placeholder="Notes sur le tempérament..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="energy_level">Niveau d'énergie (0-10)</Label>
                    <Input
                      id="energy_level"
                      type="number"
                      min="0"
                      max="10"
                      value={assessment.energy_level || ""}
                      onChange={(e) => updateField("energy_level", parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fatigue_level">Niveau de fatigue (0-10)</Label>
                    <Input
                      id="fatigue_level"
                      type="number"
                      min="0"
                      max="10"
                      value={assessment.fatigue_level || ""}
                      onChange={(e) => updateField("fatigue_level", parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stress_level">Niveau de stress</Label>
                  <Select value={assessment.stress_level} onValueChange={(value) => updateField("stress_level", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="faible">Faible</SelectItem>
                      <SelectItem value="moyen">Moyen</SelectItem>
                      <SelectItem value="eleve">Élevé</SelectItem>
                      <SelectItem value="tres_eleve">Très élevé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>État émotionnel</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {["Peur", "Tristesse", "Colère", "Joie", "Dégoût", "Surprise"].map((emotion) => (
                      <div key={emotion} className="flex items-center space-x-2">
                        <Checkbox
                          id={emotion}
                          checked={assessment.emotional_state?.[emotion] || false}
                          onCheckedChange={(checked) => {
                            updateField("emotional_state", {
                              ...assessment.emotional_state,
                              [emotion]: checked,
                            });
                          }}
                        />
                        <Label htmlFor={emotion} className="text-sm font-normal">
                          {emotion}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* NEW: Moral / Neurotransmitters */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Moral</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dopamine">Dopamine</Label>
                      <Textarea
                        id="dopamine"
                        rows={2}
                        value={assessment.moral?.dopamine || ""}
                        onChange={(e) =>
                          updateField("moral", {
                            ...assessment.moral,
                            dopamine: e.target.value,
                          })
                        }
                        placeholder="Motivation, plaisir, récompense..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serotonine">Sérotonine</Label>
                      <Textarea
                        id="serotonine"
                        rows={2}
                        value={assessment.moral?.serotonine || ""}
                        onChange={(e) =>
                          updateField("moral", {
                            ...assessment.moral,
                            serotonine: e.target.value,
                          })
                        }
                        placeholder="Humeur, bien-être, sommeil..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="noradrenaline">Noradrénaline</Label>
                      <Textarea
                        id="noradrenaline"
                        rows={2}
                        value={assessment.moral?.noradrenaline || ""}
                        onChange={(e) =>
                          updateField("moral", {
                            ...assessment.moral,
                            noradrenaline: e.target.value,
                          })
                        }
                        placeholder="Vigilance, attention, énergie..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="acetylcholine">Acétylcholine</Label>
                      <Textarea
                        id="acetylcholine"
                        rows={2}
                        value={assessment.moral?.acetylcholine || ""}
                        onChange={(e) =>
                          updateField("moral", {
                            ...assessment.moral,
                            acetylcholine: e.target.value,
                          })
                        }
                        placeholder="Mémoire, apprentissage..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="gaba">GABA</Label>
                      <Textarea
                        id="gaba"
                        rows={2}
                        value={assessment.moral?.gaba || ""}
                        onChange={(e) =>
                          updateField("moral", {
                            ...assessment.moral,
                            gaba: e.target.value,
                          })
                        }
                        placeholder="Calme, relaxation, anxiété..."
                      />
                    </div>
                  </div>
                </div>

                {/* NEW: Fleurs de Bach */}
                <div className="border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="bach_flowers">Fleurs de Bach</Label>
                    <Textarea
                      id="bach_flowers"
                      rows={3}
                      value={assessment.bach_flowers || ""}
                      onChange={(e) => updateField("bach_flowers", e.target.value)}
                      placeholder="Fleurs de Bach recommandées ou utilisées..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Systèmes */}
          <TabsContent value="systemes">
            <Card>
              <CardHeader>
                <CardTitle>Systèmes du Corps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Système Digestif */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-primary">Système Digestif</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Transit</Label>
                      <Input
                        value={assessment.digestive_system?.transit || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            transit: e.target.value,
                          })
                        }
                        placeholder="Fréquence, régularité..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bristol</Label>
                      <Select
                        value={assessment.digestive_system?.bristol || ""}
                        onValueChange={(value) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            bristol: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Échelle 1-7" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Intestins</Label>
                      <Input
                        value={assessment.digestive_system?.intestins || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            intestins: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Foie</Label>
                      <Input
                        value={assessment.digestive_system?.foie || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            foie: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pancréas</Label>
                      <Input
                        value={assessment.digestive_system?.pancreas || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            pancreas: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Estomac</Label>
                      <Input
                        value={assessment.digestive_system?.estomac || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            estomac: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mastication</Label>
                      <Input
                        value={assessment.digestive_system?.mastication || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            mastication: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Santé bucco-dentaire</Label>
                      <Input
                        value={assessment.digestive_system?.sante_bucco_dentaire || ""}
                        onChange={(e) =>
                          updateField("digestive_system", {
                            ...assessment.digestive_system,
                            sante_bucco_dentaire: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Symptômes</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {["Flatulences", "Ballonnements", "RGO", "Douleurs", "Lourdeurs", "Nausées", "Somnolence"].map(
                        (symptom) => (
                          <div key={symptom} className="flex items-center space-x-2">
                            <Checkbox
                              id={`digestive_${symptom}`}
                              checked={assessment.digestive_system?.symptoms?.[symptom] || false}
                              onCheckedChange={(checked) => {
                                updateField("digestive_system", {
                                  ...assessment.digestive_system,
                                  symptoms: {
                                    ...assessment.digestive_system?.symptoms,
                                    [symptom]: checked,
                                  },
                                });
                              }}
                            />
                            <Label htmlFor={`digestive_${symptom}`} className="text-sm font-normal">
                              {symptom}
                            </Label>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.digestive_system?.notes || ""}
                      onChange={(e) =>
                        updateField("digestive_system", {
                          ...assessment.digestive_system,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>

                {/* Système Uro-génital */}
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Système Uro-Génital</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mictions</Label>
                      <Input
                        value={assessment.urogenital_system?.mictions || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            mictions: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fréquence</Label>
                      <Input
                        value={assessment.urogenital_system?.frequence || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            frequence: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Qualité (odeur, couleur, douleur)</Label>
                      <Input
                        value={assessment.urogenital_system?.qualite || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            qualite: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Infections</Label>
                      <Input
                        value={assessment.urogenital_system?.infections || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            infections: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cycle</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {["Régulier", "Douloureux", "Abondant", "SPM"].map((item) => (
                        <div key={item} className="flex items-center space-x-2">
                          <Checkbox
                            id={`cycle_${item}`}
                            checked={assessment.urogenital_system?.cycle?.[item] || false}
                            onCheckedChange={(checked) => {
                              updateField("urogenital_system", {
                                ...assessment.urogenital_system,
                                cycle: {
                                  ...assessment.urogenital_system?.cycle,
                                  [item]: checked,
                                },
                              });
                            }}
                          />
                          <Label htmlFor={`cycle_${item}`} className="text-sm font-normal">
                            {item}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contraception</Label>
                      <Input
                        value={assessment.urogenital_system?.contraception || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            contraception: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Prostate</Label>
                      <Input
                        value={assessment.urogenital_system?.prostate || ""}
                        onChange={(e) =>
                          updateField("urogenital_system", {
                            ...assessment.urogenital_system,
                            prostate: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.urogenital_system?.notes || ""}
                      onChange={(e) =>
                        updateField("urogenital_system", {
                          ...assessment.urogenital_system,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Système ORL</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Respiration</Label>
                      <Textarea
                        rows={2}
                        value={assessment.orl_respiratory?.respiration || ""}
                        onChange={(e) =>
                          updateField("orl_respiratory", {
                            ...assessment.orl_respiratory,
                            respiration: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ORL</Label>
                      <Textarea
                        rows={2}
                        value={assessment.orl_respiratory?.orl || ""}
                        onChange={(e) =>
                          updateField("orl_respiratory", {
                            ...assessment.orl_respiratory,
                            orl: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Poumons</Label>
                      <Textarea
                        rows={2}
                        value={assessment.orl_respiratory?.poumons || ""}
                        onChange={(e) =>
                          updateField("orl_respiratory", {
                            ...assessment.orl_respiratory,
                            poumons: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.orl_respiratory?.notes || ""}
                      onChange={(e) =>
                        updateField("orl_respiratory", {
                          ...assessment.orl_respiratory,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>

                {/* Phanères */}
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Phanères</h3>
                  <div className="space-y-2">
                    <Label>Peau</Label>
                    <Select
                      value={assessment.skin_hair_nails?.peau_type || ""}
                      onValueChange={(value) =>
                        updateField("skin_hair_nails", {
                          ...assessment.skin_hair_nails,
                          peau_type: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Type de peau" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seche">Sèche</SelectItem>
                        <SelectItem value="grasse">Grasse</SelectItem>
                        <SelectItem value="mixte">Mixte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Troubles</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {["RAS", "Eczéma", "Acné", "Psoriasis", "Vitiligo"].map((trouble) => (
                        <div key={trouble} className="flex items-center space-x-2">
                          <Checkbox
                            id={`skin_${trouble}`}
                            checked={assessment.skin_hair_nails?.troubles?.[trouble] || false}
                            onCheckedChange={(checked) => {
                              updateField("skin_hair_nails", {
                                ...assessment.skin_hair_nails,
                                troubles: {
                                  ...assessment.skin_hair_nails?.troubles,
                                  [trouble]: checked,
                                },
                              });
                            }}
                          />
                          <Label htmlFor={`skin_${trouble}`} className="text-sm font-normal">
                            {trouble}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <Input
                      placeholder="Autre trouble..."
                      value={assessment.skin_hair_nails?.troubles_autre || ""}
                      onChange={(e) =>
                        updateField("skin_hair_nails", {
                          ...assessment.skin_hair_nails,
                          troubles_autre: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Transpiration</Label>
                      <Input
                        value={assessment.skin_hair_nails?.transpiration || ""}
                        onChange={(e) =>
                          updateField("skin_hair_nails", {
                            ...assessment.skin_hair_nails,
                            transpiration: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cheveux</Label>
                      <Input
                        value={assessment.skin_hair_nails?.cheveux || ""}
                        onChange={(e) =>
                          updateField("skin_hair_nails", {
                            ...assessment.skin_hair_nails,
                            cheveux: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ongles</Label>
                      <Input
                        value={assessment.skin_hair_nails?.ongles || ""}
                        onChange={(e) =>
                          updateField("skin_hair_nails", {
                            ...assessment.skin_hair_nails,
                            ongles: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.skin_hair_nails?.notes || ""}
                      onChange={(e) =>
                        updateField("skin_hair_nails", {
                          ...assessment.skin_hair_nails,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>

                {/* Système Glandulaire */}
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Système Glandulaire</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Thyroïde</Label>
                      <Input
                        value={assessment.glandular_detailed?.thyroide || ""}
                        onChange={(e) =>
                          updateField("glandular_detailed", {
                            ...assessment.glandular_detailed,
                            thyroide: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Surrénales</Label>
                      <Input
                        value={assessment.glandular_detailed?.surrenales || ""}
                        onChange={(e) =>
                          updateField("glandular_detailed", {
                            ...assessment.glandular_detailed,
                            surrenales: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Glycémie</Label>
                      <Input
                        value={assessment.glandular_detailed?.glycemie || ""}
                        onChange={(e) =>
                          updateField("glandular_detailed", {
                            ...assessment.glandular_detailed,
                            glycemie: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hormones sexuelles</Label>
                      <Input
                        value={assessment.glandular_detailed?.hormones_sexuelles || ""}
                        onChange={(e) =>
                          updateField("glandular_detailed", {
                            ...assessment.glandular_detailed,
                            hormones_sexuelles: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.glandular_detailed?.notes || ""}
                      onChange={(e) =>
                        updateField("glandular_detailed", {
                          ...assessment.glandular_detailed,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Notes complémentaires..."
                    />
                  </div>
                </div>

                {/* Autres Systèmes */}
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Autres Systèmes</h3>

                  {/* Système Ostéo-articulaire */}
                  <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium">Système Ostéo-Articulaire</h4>
                    <div className="space-y-2">
                      <Label>Troubles</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          "Douleurs",
                          "Rhumatismes",
                          "Tendinites",
                          "Entorses",
                          "Traumatismes",
                          "Ligaments",
                          "Crampes",
                          "Fractures",
                          "Ostéopénie",
                          "Ostéoporose",
                        ].map((trouble) => (
                          <div key={trouble} className="flex items-center space-x-2">
                            <Checkbox
                              id={`osteo_${trouble}`}
                              checked={assessment.musculoskeletal?.troubles?.[trouble] || false}
                              onCheckedChange={(checked) => {
                                updateField("musculoskeletal", {
                                  ...assessment.musculoskeletal,
                                  troubles: {
                                    ...assessment.musculoskeletal?.troubles,
                                    [trouble]: checked,
                                  },
                                });
                              }}
                            />
                            <Label htmlFor={`osteo_${trouble}`} className="text-sm font-normal">
                              {trouble}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={2}
                        value={assessment.musculoskeletal?.notes || ""}
                        onChange={(e) =>
                          updateField("musculoskeletal", {
                            ...assessment.musculoskeletal,
                            notes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Système Circulatoire */}
                  <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium">Systèmes Circulatoires (sang et lymphe)</h4>
                    <div className="space-y-2">
                      <Label>Extrémités</Label>
                      <Select
                        value={assessment.circulatory_system?.extremites || ""}
                        onValueChange={(value) =>
                          updateField("circulatory_system", {
                            ...assessment.circulatory_system,
                            extremites: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="chaudes">Chaudes</SelectItem>
                          <SelectItem value="froides">Froides</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Symptômes</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {["Fourmillements", "Varices", "Jambes lourdes", "Rétention d'eau"].map((symptom) => (
                          <div key={symptom} className="flex items-center space-x-2">
                            <Checkbox
                              id={`circ_${symptom}`}
                              checked={assessment.circulatory_system?.symptoms?.[symptom] || false}
                              onCheckedChange={(checked) => {
                                updateField("circulatory_system", {
                                  ...assessment.circulatory_system,
                                  symptoms: {
                                    ...assessment.circulatory_system?.symptoms,
                                    [symptom]: checked,
                                  },
                                });
                              }}
                            />
                            <Label htmlFor={`circ_${symptom}`} className="text-sm font-normal">
                              {symptom}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={2}
                        value={assessment.circulatory_system?.notes || ""}
                        onChange={(e) =>
                          updateField("circulatory_system", {
                            ...assessment.circulatory_system,
                            notes: e.target.value,
                          })
                        }
                        placeholder="Notes complémentaires..."
                      />
                    </div>
                  </div>

                  {/* Système Nerveux */}
                  <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium">Système Nerveux</h4>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={3}
                        value={assessment.nervous_system?.notes || ""}
                        onChange={(e) =>
                          updateField("nervous_system", {
                            ...assessment.nervous_system,
                            notes: e.target.value,
                          })
                        }
                        placeholder="Observations sur le système nerveux..."
                      />
                    </div>
                  </div>

                  {/* Système Cardiovasculaire */}
                  <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium">Système Cardiovasculaire</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tension</Label>
                        <Input
                          value={assessment.cardiovascular?.tension || ""}
                          onChange={(e) =>
                            updateField("cardiovascular", {
                              ...assessment.cardiovascular,
                              tension: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rythme cardiaque</Label>
                        <Input
                          value={assessment.cardiovascular?.rythme_cardiaque || ""}
                          onChange={(e) =>
                            updateField("cardiovascular", {
                              ...assessment.cardiovascular,
                              rythme_cardiaque: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={2}
                        value={assessment.cardiovascular?.notes || ""}
                        onChange={(e) =>
                          updateField("cardiovascular", {
                            ...assessment.cardiovascular,
                            notes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Système Immunitaire */}
                  <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium">Système Immunitaire</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Allergies</Label>
                        <Textarea
                          rows={2}
                          value={assessment.immune_system?.allergies || ""}
                          onChange={(e) =>
                            updateField("immune_system", {
                              ...assessment.immune_system,
                              allergies: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Intolérances</Label>
                        <Textarea
                          rows={2}
                          value={assessment.immune_system?.intolerances || ""}
                          onChange={(e) =>
                            updateField("immune_system", {
                              ...assessment.immune_system,
                              intolerances: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Infections</Label>
                      <Input
                        value={assessment.immune_system?.infections || ""}
                        onChange={(e) =>
                          updateField("immune_system", {
                            ...assessment.immune_system,
                            infections: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Infections froides</Label>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {["Lyme", "Candida", "Staphylocoque", "Herpès", "EBV"].map((infection) => (
                          <div key={infection} className="flex items-center space-x-2">
                            <Checkbox
                              id={`infection_${infection}`}
                              checked={assessment.immune_system?.infections_froides?.[infection] || false}
                              onCheckedChange={(checked) => {
                                updateField("immune_system", {
                                  ...assessment.immune_system,
                                  infections_froides: {
                                    ...assessment.immune_system?.infections_froides,
                                    [infection]: checked,
                                  },
                                });
                              }}
                            />
                            <Label htmlFor={`infection_${infection}`} className="text-sm font-normal">
                              {infection}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Antibiotiques</Label>
                        <Input
                          value={assessment.immune_system?.antibiotiques || ""}
                          onChange={(e) =>
                            updateField("immune_system", {
                              ...assessment.immune_system,
                              antibiotiques: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Maladie auto-immune</Label>
                        <Input
                          value={assessment.immune_system?.maladie_auto_immune || ""}
                          onChange={(e) =>
                            updateField("immune_system", {
                              ...assessment.immune_system,
                              maladie_auto_immune: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={2}
                        value={assessment.immune_system?.notes || ""}
                        onChange={(e) =>
                          updateField("immune_system", {
                            ...assessment.immune_system,
                            notes: e.target.value,
                          })
                        }
                        placeholder="Notes complémentaires..."
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alimentation */}
          <TabsContent value="alimentation">
            <Card>
              <CardHeader>
                <CardTitle>Bilan Alimentaire</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Évaluation */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-primary">Alimentation – Évaluation</h3>
                  
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      rows={2}
                      value={assessment.diet_evaluation?.notes || ""}
                      onChange={(e) =>
                        updateField("diet_evaluation", {
                          ...assessment.diet_evaluation,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Observations générales sur l'alimentation..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Les +</Label>
                      <Textarea
                        rows={2}
                        value={assessment.diet_evaluation?.positives || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            positives: e.target.value,
                          })
                        }
                        placeholder="Points positifs..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Les −</Label>
                      <Textarea
                        rows={2}
                        value={assessment.diet_evaluation?.negatives || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            negatives: e.target.value,
                          })
                        }
                        placeholder="Points à améliorer..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Aliments doudous</Label>
                      <Textarea
                        rows={2}
                        value={assessment.food_preferences?.likes || ""}
                        onChange={(e) =>
                          updateField("food_preferences", {
                            ...assessment.food_preferences,
                            likes: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Aliments pas aimés</Label>
                      <Textarea
                        rows={2}
                        value={assessment.food_preferences?.dislikes || ""}
                        onChange={(e) =>
                          updateField("food_preferences", {
                            ...assessment.food_preferences,
                            dislikes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Qualité du pain</Label>
                      <Input
                        value={assessment.diet_evaluation?.qualite_pain || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            qualite_pain: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Qualité de l'huile</Label>
                      <Input
                        value={assessment.diet_evaluation?.qualite_huile || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            qualite_huile: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Qualité de l'eau</Label>
                      <Input
                        value={assessment.diet_evaluation?.qualite_eau || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            qualite_eau: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Protéines</Label>
                      <Input
                        value={assessment.diet_evaluation?.proteines || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            proteines: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Légumes</Label>
                      <Input
                        value={assessment.diet_evaluation?.legumes || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            legumes: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Oméga-3</Label>
                      <Input
                        value={assessment.diet_evaluation?.omega3 || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            omega3: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Produits laitiers</Label>
                      <Input
                        value={assessment.diet_evaluation?.produits_laitiers || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            produits_laitiers: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Gluten</Label>
                      <Input
                        value={assessment.diet_evaluation?.gluten || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            gluten: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sucre</Label>
                      <Input
                        value={assessment.diet_evaluation?.sucre || ""}
                        onChange={(e) =>
                          updateField("diet_evaluation", {
                            ...assessment.diet_evaluation,
                            sucre: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Type de produits */}
                <div className="border-t pt-4 space-y-2">
                  <Label>Type de produits consommés</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {["Bio", "Conventionnel", "Industriel", "Préparé", "Surgelé", "Raffiné", "Complet"].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={type}
                          checked={assessment.food_quality?.[type] || false}
                          onCheckedChange={(checked) => {
                            updateField("food_quality", {
                              ...assessment.food_quality,
                              [type]: checked,
                            });
                          }}
                        />
                        <Label htmlFor={type} className="text-sm font-normal">
                          {type}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tableau des menus */}
                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-primary">Menus de la Semaine</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-24">Jour</TableHead>
                          <TableHead>Matin</TableHead>
                          <TableHead>Midi</TableHead>
                          <TableHead>Collation</TableHead>
                          <TableHead>Soir</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weekDays.map((day) => (
                          <TableRow key={day}>
                            <TableCell className="font-medium capitalize">{day}</TableCell>
                            {mealTimes.map((meal) => (
                              <TableCell key={meal}>
                                <Input
                                  className="min-w-[120px]"
                                  value={
                                    assessment.weekly_menu?.[day as keyof typeof assessment.weekly_menu]?.[
                                      meal as keyof (typeof assessment.weekly_menu)[keyof typeof assessment.weekly_menu]
                                    ] || ""
                                  }
                                  onChange={(e) => updateWeeklyMenu(day, meal, e.target.value)}
                                  placeholder="..."
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Boissons */}
                <div className="border-t pt-4 space-y-2">
                  <Label htmlFor="beverages">Boissons</Label>
                  <Textarea
                    id="beverages"
                    rows={2}
                    value={assessment.meals?.beverages || ""}
                    onChange={(e) =>
                      updateField("meals", {
                        ...assessment.meals,
                        beverages: e.target.value,
                      })
                    }
                    placeholder="Eau, café, thé, sodas, alcool..."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Récapitulatif */}
          <TabsContent value="recap">
            <Card>
              <CardHeader>
                <CardTitle>Récapitulatif et Plan d'Action</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="main_issues">Motif et problématiques principales</Label>
                  <Textarea
                    id="main_issues"
                    rows={3}
                    value={assessment.main_issues || ""}
                    onChange={(e) => updateField("main_issues", e.target.value)}
                    placeholder="Synthèse des principales problématiques..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="treatment_axes">Pistes et axes de travail</Label>
                  <Textarea
                    id="treatment_axes"
                    rows={3}
                    value={assessment.treatment_axes || ""}
                    onChange={(e) => updateField("treatment_axes", e.target.value)}
                    placeholder="Stress, dysbiose, inflammation, foie, équilibre acido-basique, alimentation hypotoxique, microbiote, relaxation, détox, reminéralisation..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="psycho_emotional_plan">Plan psycho-émotionnel</Label>
                  <Textarea
                    id="psycho_emotional_plan"
                    rows={3}
                    value={assessment.psycho_emotional_plan || ""}
                    onChange={(e) => updateField("psycho_emotional_plan", e.target.value)}
                    placeholder="Gestion du stress, émotions, relaxation..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nutrition_plan">Plan alimentaire</Label>
                  <Textarea
                    id="nutrition_plan"
                    rows={3}
                    value={assessment.nutrition_plan || ""}
                    onChange={(e) => updateField("nutrition_plan", e.target.value)}
                    placeholder="Recommandations nutritionnelles..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="physical_activity_plan">Plan d'activité physique</Label>
                  <Textarea
                    id="physical_activity_plan"
                    rows={3}
                    value={assessment.physical_activity_plan || ""}
                    onChange={(e) => updateField("physical_activity_plan", e.target.value)}
                    placeholder="Recommandations d'activités..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supplements_plan">Compléments alimentaires recommandés</Label>
                  <Textarea
                    id="supplements_plan"
                    rows={3}
                    value={assessment.supplements_plan || ""}
                    onChange={(e) => updateField("supplements_plan", e.target.value)}
                    placeholder="Suppléments, posologie, durée..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="other_techniques">Autres techniques</Label>
                  <Textarea
                    id="other_techniques"
                    rows={3}
                    value={assessment.other_techniques || ""}
                    onChange={(e) => updateField("other_techniques", e.target.value)}
                    placeholder="Réflexologie, aromathérapie, phytothérapie..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="followup_questions">Questions pour le suivi</Label>
                  <Textarea
                    id="followup_questions"
                    rows={3}
                    value={assessment.followup_questions || ""}
                    onChange={(e) => updateField("followup_questions", e.target.value)}
                    placeholder="Questions à poser lors du prochain rendez-vous..."
                  />
                </div>

                {/* Boutons Enregistrer et Exporter PDF */}
                <div className="border-t pt-6">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      onClick={handleSave} 
                      disabled={isSaving}
                      className="gap-2"
                      size="lg"
                    >
                      <Save className="w-5 h-5" />
                      {isSaving ? "Enregistrement..." : "Enregistrer le bilan"}
                    </Button>
                    <Button 
                      onClick={handleExportPDF} 
                      variant="outline"
                      className="gap-2"
                      size="lg"
                    >
                      <FileDown className="w-5 h-5" />
                      Exporter en PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Navigation Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  const currentIndex = tabs.findIndex((tab) => tab.id === currentTab);
                  if (currentIndex > 0) {
                    setCurrentTab(tabs[currentIndex - 1].id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                disabled={currentTabIndex === 0}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Précédent
              </Button>

              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                variant="secondary"
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? "..." : "Enregistrer"}
              </Button>

              <Button
                onClick={() => {
                  const currentIndex = tabs.findIndex((tab) => tab.id === currentTab);
                  if (currentIndex < tabs.length - 1) {
                    setCurrentTab(tabs[currentIndex + 1].id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                disabled={currentTabIndex === tabs.length - 1}
                className="gap-2"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default VitalityAssessmentPage;
